/**
 * Design tokens — the single source of truth for the redesign.
 *
 * Two halves:
 * - `tokens` holds the **mode-agnostic** structural scales (type, spacing,
 *   radius, shadow, breakpoint). Import these directly anywhere.
 * - `skueyLight` / `skueyDark` hold the **color palette**, which depends on the
 *   active theme mode. These are injected into the MUI theme as `theme.skuey`,
 *   so components read colors from the theme (`theme.skuey.ink`, …) and never
 *   hardcode a hex. That's what lets light/dark switching reach every surface.
 *
 * Derived from the auth mocks: a calm, rounded, royal-blue brand on near-white
 * (light) or slate-navy (dark) surfaces.
 */

// Brand ramp — royal blue, identical in both modes.
const brand = {
  50: "#EFF6FF",
  100: "#DBEAFE",
  500: "#3B82F6",
  600: "#2563EB", // primary
  700: "#1D4ED8", // hover / pressed
} as const;

// Fixed colors that don't change with the mode.
const fixed = {
  brand,
  white: "#FFFFFF",
  success: "#16A34A",
  danger: "#DC2626",
  dangerBg: "#FEF2F2",
  warning: "#D97706",
} as const;

// Mode-dependent semantic roles. Same keys in both sets so `theme.skuey` has a
// stable shape; only the values differ.
type Semantic = {
  ink: string;
  body: string;
  muted: string;
  subtle: string;
  border: string;
  borderStrong: string;
  surface: string;
  surfaceAlt: string;
  brandSubtleBg: string;
  glassBg: string;
  glassBorder: string;
  brandPanelOverlay: string; // gradient laid over the desktop hero photo
};

const light: Semantic = {
  ink: "#0F172A", // headings
  body: "#334155", // body text
  muted: "#6a6c73", // secondary text
  subtle: "#94A3B8", // placeholders / disabled
  border: "#E2E8F0",
  borderStrong: "#CBD5E1",
  surface: "#FFFFFF", // cards / inputs
  surfaceAlt: "#F8FAFC", // page background
  brandSubtleBg: "#EFF6FF", // tinted brand surface (icon chips, ghost hover)
  glassBg: "rgba(255,255,255,0.88)", // glass card over the hero
  glassBorder: "rgba(255,255,255,0.7)",
  // Angled fade: the photo dissolves out to a solid light tint on the left
  // (where the wordmark/copy sit) and stays visible toward the right, like the
  // mobile mask. Tune the stop positions to reveal more/less of the image.
  brandPanelOverlay: `linear-gradient(70deg,
    rgb(237 ,245, 253,1) 0%,
    rgb(237, 245, 253,1) 30%,
    rgb(237, 245 ,253,1) 40%,
    rgb(237, 245, 253,.9) 55%,
    rgba(11,17,32,0) 100%)`,
};

const dark: Semantic = {
  ink: "#F8FAFC",
  body: "#CBD5E1",
  muted: "#94A3B8",
  subtle: "#64748B",
  border: "#1E293B",
  borderStrong: "#334155",
  surface: "#111827",
  surfaceAlt: "#0B1120",
  brandSubtleBg: "rgba(59,130,246,0.16)",
  glassBg: "rgba(17,24,39,0.72)",
  glassBorder: "rgba(148,163,184,0.12)",
  brandPanelOverlay:
    "linear-gradient(165deg, rgba(15,23,42,0.55) 0%, rgba(11,17,32,0.90) 50%, rgba(11,17,32,0.98) 100%)",
};

/** Full color palette for a mode — `theme.skuey` resolves to one of these. */
export const skueyLight = { ...fixed, ...light } as const;
export const skueyDark = { ...fixed, ...dark } as const;

/** Shape of `theme.skuey` (light and dark share it by construction). */
export type SkueyTokens = typeof skueyLight;

export const tokens = {
  font: {
    // System stack — native, fast, no webfont payload (SF on Apple, Segoe on
    // Windows). Swap to Inter via @fontsource here if we want cross-platform parity.
    family:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, system-ui, sans-serif',
    weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
    // size / lineHeight pairs (px)
    size: {
      display: [28, 34] as const,
      h2: [22, 28] as const,
      h3: [18, 26] as const,
      body: [15, 22] as const,
      small: [13, 18] as const,
      tiny: [12, 16] as const,
      micro: [11, 14] as const,
    },
  },

  // 4px base spacing scale.
  space: (n: number) => n * 4,

  radius: {
    xs: 4,
    sm: 8,
    md: 12, // controls (buttons, inputs)
    lg: 16,
    xl: 24, // cards / sheets
    full: 9999,
  },

  shadow: {
    sm: "0 1px 2px rgba(15,23,42,0.06)",
    md: "0 4px 12px rgba(15,23,42,0.08)",
    lg: "0 12px 32px rgba(15,23,42,0.12)",
  },

  // Matches the app-wide mobile breakpoint used elsewhere.
  breakpoint: { mobile: 768 },
} as const;

export type Tokens = typeof tokens;
